import { useRef, useState, type SubmitEvent } from 'react'
import {
  AppLayout,
  Mtds,
  ScreenHeader,
  ScreenLayout,
  TopBar,
  getEnvName,
  toUserMessage,
  UserMessageError,
  useCreateResultsMutation
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import {
  buildResultsRequest,
  createResult,
  createResultLine,
  createResultsDraft,
  validateResults,
  isResultsReceipt,
  type ResultsDraft,
  type ResultsReceipt,
  type ToolErrors
} from './developer-tools/resultsUpload'
import { ToolErrorSummary } from './developer-tools/ToolErrorSummary'
import { useToolUnsavedChanges } from './developer-tools/useToolUnsavedChanges'
import { ResultsReceiptCard } from './developer-tools/ResultsReceiptCard'
import { AttachmentUploadCard } from './developer-tools/AttachmentUploadCard'

const interpretations = [
  { value: '', label: 'Utelat feltet' },
  { value: 'pos', label: 'pos (positiv)' },
  { value: 'neg', label: 'neg (negativ)' },
  { value: 'inc', label: 'inc (inkonklusiv)' },
  { value: 'null', label: 'null (ingen forslag)' }
]
const lineFields = [
  {
    key: 'name',
    label: 'Navn',
    description: 'Menneskelesbart substansnavn.',
    required: true
  },
  { key: 'efsaCode', label: 'EFSA-kode', description: 'Kode fra EFSA SSD2.' },
  {
    key: 'amount',
    label: 'Mengde',
    description: 'Valgfritt tall. Bruk desimalpunktum.'
  },
  {
    key: 'detectableThreshold',
    label: 'Deteksjonsgrense',
    description: 'Valgfritt tall. Bruk desimalpunktum.'
  },
  {
    key: 'unitOfMeasurement',
    label: 'Måleenhet',
    description: 'For eksempel mg/kg.',
    required: true
  },
  {
    key: 'method',
    label: 'Metode',
    description: 'Labmetode, for eksempel SOP-123.',
    required: true
  }
] as const

export const ResultsUploadScreen = () => {
  const createResults = useCreateResultsMutation()
  const [draft, setDraftState] = useState(createResultsDraft)
  const [savedDraft, setSavedDraft] = useState(() => JSON.stringify(draft))
  const [errors, setErrors] = useState<ToolErrors>({})
  const [resultError, setResultError] = useState('')
  const [receipt, setReceipt] = useState<{
    requisitionId: string
    data: ResultsReceipt
  }>()
  const [submitted, setSubmitted] = useState(false)
  const [attachmentStatus, setAttachmentStatus] = useState({
    dirty: false,
    pending: false
  })
  const [resetKey, setResetKey] = useState(0)
  const writing = useRef(false)
  const pending = createResults.isPending || attachmentStatus.pending
  const dirty = JSON.stringify(draft) !== savedDraft || attachmentStatus.dirty
  useToolUnsavedChanges(dirty, pending)
  const env = getEnvName()
  const production = env === undefined

  const setDraft = (next: ResultsDraft) => {
    setDraftState(next)
    setErrors({})
  }

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (writing.current || (submitted && JSON.stringify(draft) === savedDraft))
      return
    const nextErrors = validateResults(draft)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return
    const request = buildResultsRequest(draft)
    if (
      production &&
      !window.confirm(
        `PRODUKSJON: Send ${draft.results.length} resultat(er) til rekvisisjon ${request.requisitionId}, prøve ${request.body.id}? Dette skriver reelle data.`
      )
    )
      return
    writing.current = true
    setResultError('')
    try {
      const data = await createResults.mutateAsync(request)
      if (!isResultsReceipt(data, request.body.id)) {
        throw new UserMessageError(
          'Innsendingen kunne ikke bekreftes: serveren returnerte ingen gyldig kvittering.'
        )
      }
      setSavedDraft(JSON.stringify(draft))
      setSubmitted(true)
      setReceipt({ requisitionId: String(request.requisitionId), data })
    } catch (error) {
      setResultError(
        `${toUserMessage(error, 'sende inn resultatene')} Utkastet er beholdt. Kontroller rekvisisjonen før nytt forsøk; ved nettverksfeil kan data allerede være lagret.`
      )
    } finally {
      writing.current = false
    }
  }

  const reset = () => {
    if (
      writing.current ||
      (dirty &&
        !window.confirm(
          'Forkaste ulagrede endringer og valgt vedlegg? Innsendte data slettes ikke.'
        ))
    )
      return
    const empty = createResultsDraft()
    setDraft(empty)
    setSavedDraft(JSON.stringify(empty))
    setSubmitted(false)
    setReceipt(undefined)
    setErrors({})
    setResultError('')
    setResetKey((key) => key + 1)
  }

  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      topBar={<TopBar logo={<AppLogo />} title='Resultater' />}
      useMobileBar={false}
      isFullWidth
    >
      <ScreenLayout
        header={
          <ScreenHeader
            title='Last opp resultater'
            description={
              <p>
                Utviklerverktøy for resultat-API v2 og PDF-vedlegg. Ingen data
                sendes før du velger en skrivehandling.
              </p>
            }
          />
        }
      >
        <Mtds.Grid data-size='sm' data-gap='4'>
          <Mtds.Group as={Mtds.Grid} data-pad='4'>
            <Mtds.Info>
              <strong>Miljø</strong>
              {env === 'dev'
                ? 'Utvikling'
                : env === 'training'
                  ? 'Opplæring'
                  : 'Produksjon / ukjent miljø'}
            </Mtds.Info>
            {production && (
              <Mtds.Alert data-color='warning'>
                Produksjonsvern er aktivt. Resultater og vedlegg skriver reelle
                data. Hver skrivehandling krever bekreftelse.
              </Mtds.Alert>
            )}
            <Mtds.Flex data-justify='space-between' data-align='center'>
              <Mtds.Muted>
                Utkast lagres kun i denne fanen. Nullstilling sletter ikke
                innsendte data.
              </Mtds.Muted>
              <Mtds.Button
                type='button'
                data-variant='secondary'
                disabled={pending}
                onClick={reset}
              >
                Nullstill verktøy
              </Mtds.Button>
            </Mtds.Flex>
          </Mtds.Group>
          <Mtds.Grid as='form' noValidate onSubmit={handleSubmit} data-gap='4'>
            <Mtds.Card as={Mtds.Grid} data-pad='4'>
              <Mtds.Heading as='h2' data-size='sm'>
                1. Send resultater
              </Mtds.Heading>
              <ToolErrorSummary errors={errors} />
              <Mtds.Grid data-items='250'>
                <Mtds.Field
                  as='input'
                  id='requisitionId'
                  label='Rekvisisjons-ID'
                  description='Heltall fra rekvisisjonen.'
                  required
                  disabled={pending}
                  value={draft.requisitionId}
                  validation={errors.requisitionId}
                  onChange={(event) =>
                    setDraft({ ...draft, requisitionId: event.target.value })
                  }
                />
                <Mtds.Field
                  as='input'
                  id='sampleId'
                  label='Prøve-ID'
                  description='Unik prøve-ID, typisk posenummer.'
                  required
                  disabled={pending}
                  value={draft.sampleId}
                  validation={errors.sampleId}
                  onChange={(event) =>
                    setDraft({ ...draft, sampleId: event.target.value })
                  }
                />
              </Mtds.Grid>
            </Mtds.Card>
            {draft.results.map((result, resultIndex) => {
              const prefix = `result-${result.id}`
              const update = (patch: Partial<typeof result>) =>
                setDraft({
                  ...draft,
                  results: draft.results.map((item) =>
                    item.id === result.id ? { ...item, ...patch } : item
                  )
                })
              return (
                <Mtds.Card
                  as={Mtds.Grid}
                  key={result.id}
                  data-pad='4'
                  data-gap='4'
                >
                  <Mtds.Flex data-justify='space-between' data-align='center'>
                    <Mtds.Heading as='h3' data-size='xs'>
                      Resultat {resultIndex + 1}
                    </Mtds.Heading>
                    <Mtds.Button
                      type='button'
                      data-variant='secondary'
                      disabled={pending || draft.results.length === 1}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          results: draft.results.filter(
                            (item) => item.id !== result.id
                          )
                        })
                      }
                    >
                      Fjern resultat {resultIndex + 1}
                    </Mtds.Button>
                  </Mtds.Flex>
                  <Mtds.Grid data-items='250'>
                    <Mtds.Field
                      as='input'
                      id={`${prefix}-subSampleId`}
                      label='Delprøve-ID'
                      description='Må samsvare med delprøven. Uten delprøver: utelat eller bruk 0.'
                      disabled={pending}
                      value={result.subSampleId}
                      validation={errors[`${prefix}-subSampleId`]}
                      onChange={(event) =>
                        update({ subSampleId: event.target.value })
                      }
                    />
                    <Mtds.Field
                      as='input'
                      label='Substans'
                      description='Substanskode i rekvisisjonen, for eksempel EFSA-kode.'
                      disabled={pending}
                      value={result.substance}
                      onChange={(event) =>
                        update({ substance: event.target.value })
                      }
                    />
                    <Mtds.Field
                      as='select'
                      label='Foreslått tolkning'
                      disabled={pending}
                      options={interpretations}
                      value={result.suggestedInterpretation}
                      onChange={(event) =>
                        update({ suggestedInterpretation: event.target.value })
                      }
                    />
                  </Mtds.Grid>
                  {!result.resultLines.length && (
                    <Mtds.Muted>
                      Ingen resultatlinjer. API-et tillater en tom liste.
                    </Mtds.Muted>
                  )}
                  {result.resultLines.map((line, lineIndex) => (
                    <Mtds.Group as={Mtds.Grid} key={lineIndex} data-pad='3'>
                      <Mtds.Flex
                        data-justify='space-between'
                        data-align='center'
                      >
                        <Mtds.Heading as='h4' data-size='xs'>
                          Linje {lineIndex + 1}
                        </Mtds.Heading>
                        <Mtds.Button
                          type='button'
                          data-variant='secondary'
                          disabled={pending}
                          onClick={() =>
                            update({
                              resultLines: result.resultLines.filter(
                                (_, index) => index !== lineIndex
                              )
                            })
                          }
                        >
                          Fjern linje {lineIndex + 1}
                        </Mtds.Button>
                      </Mtds.Flex>
                      <Mtds.Grid data-items='250'>
                        {lineFields.map(
                          ({ key, label, description, ...props }) => (
                            <Mtds.Field
                              as='input'
                              key={key}
                              id={`${prefix}-${lineIndex}-${key}`}
                              label={label}
                              description={description}
                              {...props}
                              disabled={pending}
                              value={line[key]}
                              validation={
                                errors[`${prefix}-${lineIndex}-${key}`]
                              }
                              onChange={(event) =>
                                update({
                                  resultLines: result.resultLines.map(
                                    (item, index) =>
                                      index === lineIndex
                                        ? { ...item, [key]: event.target.value }
                                        : item
                                  )
                                })
                              }
                            />
                          )
                        )}
                        <Mtds.Field
                          as='select'
                          label='Foreslått tolkning'
                          options={interpretations}
                          disabled={pending}
                          value={line.suggestedInterpretation}
                          onChange={(event) =>
                            update({
                              resultLines: result.resultLines.map(
                                (item, index) =>
                                  index === lineIndex
                                    ? {
                                        ...item,
                                        suggestedInterpretation:
                                          event.target.value
                                      }
                                    : item
                              )
                            })
                          }
                        />
                        <Mtds.Field
                          as='input'
                          type='checkbox'
                          label='Under terskel'
                          description='Verdien er under målbar terskel.'
                          disabled={pending}
                          checked={line.belowThreshold}
                          onChange={(event) =>
                            update({
                              resultLines: result.resultLines.map(
                                (item, index) =>
                                  index === lineIndex
                                    ? {
                                        ...item,
                                        belowThreshold: event.target.checked
                                      }
                                    : item
                              )
                            })
                          }
                        />
                      </Mtds.Grid>
                    </Mtds.Group>
                  ))}
                  <Mtds.Flex>
                    <Mtds.Button
                      type='button'
                      data-variant='secondary'
                      disabled={pending}
                      onClick={() =>
                        update({
                          resultLines: [
                            ...result.resultLines,
                            createResultLine()
                          ]
                        })
                      }
                    >
                      Legg til resultatlinje
                    </Mtds.Button>
                  </Mtds.Flex>
                </Mtds.Card>
              )
            })}
            <Mtds.Card as={Mtds.Grid} data-pad='4'>
              {resultError && (
                <Mtds.Alert data-color='danger' role='alert'>
                  {resultError}
                </Mtds.Alert>
              )}
              <Mtds.Flex>
                <Mtds.Button
                  type='submit'
                  data-variant='primary'
                  aria-busy={createResults.isPending}
                  disabled={
                    pending ||
                    (submitted && JSON.stringify(draft) === savedDraft)
                  }
                >
                  {createResults.isPending
                    ? 'Sender resultater…'
                    : 'Send resultater'}
                </Mtds.Button>
                <Mtds.Button
                  id='add-result'
                  type='button'
                  data-variant='secondary'
                  disabled={pending}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      results: [...draft.results, createResult()]
                    })
                  }
                >
                  Legg til resultat
                </Mtds.Button>
              </Mtds.Flex>
              {submitted && (
                <Mtds.Muted>
                  Utkastet er beholdt. Uendret innsending er sperret for å
                  hindre utilsiktet gjentakelse.
                </Mtds.Muted>
              )}
            </Mtds.Card>
          </Mtds.Grid>
          {receipt && <ResultsReceiptCard receipt={receipt} />}
          <AttachmentUploadCard
            receipt={receipt}
            disabled={createResults.isPending}
            resetKey={resetKey}
            onStatusChange={setAttachmentStatus}
          />
        </Mtds.Grid>
      </ScreenLayout>
    </AppLayout>
  )
}
